/**
 * Los datos de la ficha de un foco: el foco, sus partes, sus tareas y las
 * pulverizaciones de su sector. La historia la monta `buildFocusTimeline`.
 *
 * SE COMPRUEBA EL ALCANCE DE VERDAD, no se declara exento: a esta ficha se llega
 * tocando una chincheta —que ya viene recortada— pero también por enlace
 * directo, y un encargado de Las Norias no tiene por qué poder abrir un foco de
 * Campohermoso pegando una dirección.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLocationScope } from "@/hooks/useLocationScope";
import { claveAlcance, enAlcance } from "@/lib/alcance";
import { buildPestCanon, type RawPest } from "@/lib/health/pestCanon";
import { buildFocusTimeline, type PendingReview, type TimelineDecision, type TimelineItem, type TimelineSession, type TimelineSpray, type TimelineTask } from "@/lib/health/focusTimeline";
import type { FocusEvolution, LineState } from "@/lib/health/farmHealthModel";
import { saidVerdict } from "@/lib/pests/sessionReport";

export interface FocusDetail {
  id: string;
  focusNumber: number;
  pestId: string | null;
  pestName: string;
  greenhouseId: string;
  greenhouseName: string | null;
  locationName: string | null;
  blockId: string;
  blockName: string | null;
  lineNumber: number | null;
  reopenCount: number;
  sessions: TimelineSession[];
  timeline: TimelineItem[];
  /** Manda el muestreo más reciente. */
  state: LineState;
  latestSessionId: string | null;
  firstDate: string | null;
  people: Record<string, string>;
}

/** El estado de UN parte, con la misma regla que el mapa (`farmHealthModel`). También lo usa la ficha del muestreo (`useSessionFocus`). */
export function stateOf(s: { review_status: string; worker_resolved_at: string | null; linked_task_id: string | null }, taskStatus: string | null): LineState {
  if (s.review_status === "resolved") return "resolved";
  if (s.worker_resolved_at != null) return "treated";
  if (taskStatus === "completed") return "treated";
  if (taskStatus === "cancelled") return "open";
  return s.linked_task_id ? "planned" : "open";
}

export function useFocusTimeline(focusId: string | null) {
  const { company } = useAuth();
  const { allowedLocationIds, excludedBlockIds, loading: scopeLoading } = useLocationScope();

  return useQuery({
    queryKey: ["focus-sheet", focusId, company?.id, claveAlcance(allowedLocationIds, excludedBlockIds)],
    enabled: !!focusId && !!company?.id && !scopeLoading,
    staleTime: 15_000,
    queryFn: async (): Promise<FocusDetail | null> => {
      const { data: f, error } = await supabase
        .from("pest_foci")
        .select("id, focus_number, pest_id, pest_name_snapshot, greenhouse_id, block_id, line_number, reopen_count, greenhouse:greenhouses(name, location_id, location:locations(name)), block:blocks(name)")
        .eq("id", focusId!)
        .eq("company_id", company!.id)
        .maybeSingle();
      if (error) throw error;
      if (!f) return null;
      const row = f as unknown as {
        id: string; focus_number: number; pest_id: string | null; pest_name_snapshot: string; greenhouse_id: string; block_id: string;
        line_number: number | null; reopen_count: number;
        greenhouse: { name: string | null; location_id: string | null; location: { name: string | null } | null } | null;
        block: { name: string | null } | null;
      };
      // Fuera de su alcance, esta ficha no existe para él.
      if (!enAlcance({ block_id: row.block_id, location_id: row.greenhouse?.location_id ?? null }, allowedLocationIds, excludedBlockIds)) return null;

      const { data: rawSessions, error: sErr } = await supabase
        .from("pest_scouting_sessions")
        .select("id, date, created_at, severity, triage_status, dismissal_reason, review_status, worker_resolved_at, linked_task_id, followup_task_id, evolution_verdict, evolution_verdict_by, followup_answer, resolved_at, inspector_id, photo_url, photo_urls, block_row:block_rows(line_number)")
        .eq("focus_id", row.id)
        .eq("company_id", company!.id)
        .order("date")
        .order("created_at");
      if (sErr) throw sErr;
      type S = {
        id: string; date: string; created_at: string | null; severity: string | null; triage_status: string; dismissal_reason: string | null;
        review_status: string; worker_resolved_at: string | null; linked_task_id: string | null; followup_task_id: string | null;
        evolution_verdict: string | null; evolution_verdict_by: string | null; followup_answer: string | null;
        resolved_at: string | null; inspector_id: string | null; photo_url: string | null; photo_urls: string[] | null;
        block_row: { line_number: number | null } | null;
      };
      const members = ((rawSessions ?? []) as unknown as S[]).filter(
        (s) => s.triage_status === "classified" || (s.triage_status === "dismissed" && s.dismissal_reason === "clean_check"),
      );

      const linked = [...new Set(members.map((s) => s.linked_task_id).filter(Boolean))] as string[];
      const taskCols = "id, type, status, title, scheduled_date, created_at, completed_at, assigned_to, followup_draft";
      // Quién cerró y por qué, quién reabrió (D14) y las revisiones enviadas sin confirmar: todo
      // opcional y NO fatal (la RLS del rastro solo deja leer a admin y encargado).
      const decisionsQ = supabase
        .from("focus_link_decisions")
        .select("action, decided_by, decided_at, detail")
        .eq("focus_id", row.id)
        // Y lo que antes desaparecía sin rastro (23-09-2026): tareas quitadas, focos unidos, separaciones.
        .in("action", ["close", "reopen", "clean_check", "task_removed", "merge", "split", "split_out"])
        .then((r) => (r.error ? [] : (r.data ?? [])), () => []);
      const pendingQ = supabase
        .from("pest_scouting_sessions")
        .select("id, date, inspector_id, evolution_verdict, evolution_verdict_by, followup_answer")
        .eq("followup_focus_id", row.id)
        .eq("triage_status", "pending_triage")
        .then((r) => (r.error ? [] : (r.data ?? [])), () => []);
      const [byFocus, byLink, spraysRes, pestsRes, decisionsRaw, pendingRaw] = await Promise.all([
        supabase.from("planner_tasks").select(taskCols).eq("focus_id", row.id).eq("company_id", company!.id),
        linked.length ? supabase.from("planner_tasks").select(taskCols).in("id", linked) : Promise.resolve({ data: [], error: null }),
        supabase
          .from("spray_logs")
          .select("id, date, task_id, spray_log_entries(product_name_snapshot, pest_id, pest_name_snapshot)")
          .eq("block_id", row.block_id)
          .gte("date", members[0]?.date ?? "9999-12-31"),
        supabase.from("pests").select("id, name, scientific_name"),
        decisionsQ,
        pendingQ,
      ]);
      const decisions = (decisionsRaw as unknown as TimelineDecision[]) ?? [];
      const pendingReviews: PendingReview[] = ((pendingRaw as unknown as { id: string; date: string; inspector_id: string | null; evolution_verdict: string | null; evolution_verdict_by: string | null; followup_answer: string | null }[]) ?? []).map((p) => ({
        id: p.id,
        date: p.date,
        inspector_id: p.inspector_id,
        // «No lo sé» incluido (24-09-2026): una revisión enviada sin avance dijo eso.
        verdict: saidVerdict(p),
      }));
      const tasks = new Map<string, TimelineTask>();
      for (const t of [...((byFocus.data ?? []) as unknown as TimelineTask[]), ...((byLink.data ?? []) as unknown as TimelineTask[])]) tasks.set(t.id, t);
      // Los tratamientos que se anularon al CERRAR el foco (E) ya no cuelgan de ningún parte:
      // se piden por el id que guardó la decisión, para que la historia diga «anulado».
      const cancelledIds = [...new Set(decisions.flatMap((d) => d.detail?.cancelled_task_ids ?? []))].filter((id) => !tasks.has(id));
      if (cancelledIds.length) {
        const { data: cancelled } = await supabase.from("planner_tasks").select(taskCols).in("id", cancelledIds);
        for (const t of (cancelled ?? []) as unknown as TimelineTask[]) tasks.set(t.id, t);
      }

      const canon = buildPestCanon((pestsRes.data ?? []) as unknown as RawPest[]);
      const sprays: TimelineSpray[] = ((spraysRes.data ?? []) as unknown as {
        id: string; date: string; task_id: string | null;
        spray_log_entries: { product_name_snapshot: string | null; pest_id: string | null; pest_name_snapshot: string | null }[];
      }[]).map((sp) => ({
        id: sp.id,
        date: sp.date,
        task_id: sp.task_id,
        products: [...new Set(sp.spray_log_entries.map((e) => e.product_name_snapshot).filter(Boolean) as string[])],
        pestKeys: [...new Set(sp.spray_log_entries.map((e) => canon.key(e.pest_id, e.pest_name_snapshot)).filter(Boolean) as string[])],
      }));

      const sessions: TimelineSession[] = members.map((s) => ({
        id: s.id,
        date: s.date,
        created_at: s.created_at,
        kind: s.triage_status === "dismissed" ? "clean_check" : "sampling",
        state: stateOf(s, s.linked_task_id ? tasks.get(s.linked_task_id)?.status ?? null : null),
        severity: s.severity,
        // Lo que dijo quien muestreó, «No lo sé» incluido: nunca un «Igual» sacado de la gravedad (24-09-2026).
        verdict: saidVerdict(s) as FocusEvolution | null,
        line_number: s.block_row?.line_number ?? null,
        inspector_id: s.inspector_id,
        photo_url: s.photo_urls?.[0] ?? s.photo_url ?? null,
        linked_task_id: s.linked_task_id,
        followup_task_id: s.followup_task_id,
        resolved_at: s.resolved_at,
      }));

      const ids = [
        ...new Set(
          [
            ...sessions.map((s) => s.inspector_id),
            ...[...tasks.values()].map((t) => t.assigned_to),
            ...decisions.map((d) => d.decided_by),
            ...pendingReviews.map((p) => p.inspector_id),
          ].filter(Boolean),
        ),
      ] as string[];
      const people: Record<string, string> = {};
      if (ids.length) {
        const { data: profs } = await supabase.from("profiles").select("user_id, full_name").in("user_id", ids);
        for (const p of (profs ?? []) as { user_id: string; full_name: string | null }[]) if (p.full_name) people[p.user_id] = p.full_name;
      }

      const samplings = sessions.filter((s) => s.kind === "sampling");
      const latest = samplings[samplings.length - 1] ?? null;
      return {
        id: row.id,
        focusNumber: row.focus_number,
        pestId: row.pest_id,
        pestName: row.pest_name_snapshot,
        greenhouseId: row.greenhouse_id,
        greenhouseName: row.greenhouse?.name ?? null,
        locationName: row.greenhouse?.location?.name ?? null,
        blockId: row.block_id,
        blockName: row.block?.name ?? null,
        lineNumber: row.line_number,
        reopenCount: row.reopen_count,
        sessions,
        timeline: buildFocusTimeline({ sessions, tasks: [...tasks.values()], sprays, pestKey: canon.key(row.pest_id, row.pest_name_snapshot), decisions, pendingReviews }),
        state: latest?.state ?? "no_data",
        latestSessionId: latest?.id ?? null,
        firstDate: sessions[0]?.date ?? null,
        people,
      };
    },
  });
}
